import Papa from 'papaparse';

const HEADER_ALIASES = {
  companyid: 'company_id',
  company: 'company_id',
  company_id: 'company_id',
  date: 'date',
  coresales: 'core_sales',
  core_sales: 'core_sales',
  cateringsales: 'catering_sales',
  catering_sales: 'catering_sales',
  totalsales: 'total_sales',
  total_sales: 'total_sales',
  netsales: 'total_sales',
};

const REQUIRED = ['company_id', 'date', 'core_sales', 'catering_sales', 'total_sales'];

function normalizeHeader(name) {
  return name.toLowerCase().replace(/[\s_-]/g, '');
}

export function parseDecimal(raw) {
  const cleaned = String(raw == null ? '' : raw).trim().replace(/^\$/, '').replace(/,/g, '');
  if (!cleaned) return { value: null, error: 'Required' };
  const n = Number(cleaned);
  if (isNaN(n)) return { value: null, error: `"${String(raw).trim()}" is not a valid number` };
  if (n < 0) return { value: null, error: 'Must be non-negative' };
  // Round to 2 decimals, store as string to preserve precision
  return { value: n.toFixed(2) };
}

export function parseDate(raw) {
  const val = String(raw == null ? '' : raw).trim();
  if (!val) return { value: null, error: 'Required' };

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const d = new Date(`${val}T00:00:00`);
    if (isNaN(d.getTime())) return { value: null, error: `Invalid date "${val}"` };
    return { value: val };
  }
  // MM/DD/YYYY
  const m4 = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m4) {
    const [, mm, dd, yyyy] = m4;
    return { value: `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}` };
  }
  // MM/DD/YY
  const m2 = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (m2) {
    const [, mm, dd, yy] = m2;
    const yyyy = parseInt(yy) >= 50 ? `19${yy}` : `20${yy}`;
    return { value: `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}` };
  }
  return { value: null, error: `Unrecognized date "${val}" — expected YYYY-MM-DD` };
}

export function validateRow(row, companiesSet) {
  const errors = {};
  const warnings = [];

  const cid = String(row.company_id || '').trim();
  if (!cid) {
    errors.company_id = 'Required';
  } else if (companiesSet && companiesSet.size > 0 && !companiesSet.has(cid.toLowerCase())) {
    errors.company_id = 'Unknown company ID';
  }

  const dateResult = parseDate(row.date);
  if (dateResult.error) errors.date = dateResult.error;

  const csResult = parseDecimal(row.core_sales);
  if (csResult.error) errors.core_sales = csResult.error;

  const catResult = parseDecimal(row.catering_sales);
  if (catResult.error) errors.catering_sales = catResult.error;

  const totResult = parseDecimal(row.total_sales);
  if (totResult.error) errors.total_sales = totResult.error;

  if (!errors.core_sales && !errors.catering_sales && !errors.total_sales) {
    const sum = Math.round((Number(csResult.value) + Number(catResult.value)) * 100) / 100;
    const tot = Math.round(Number(totResult.value) * 100) / 100;
    if (Math.abs(sum - tot) > 0.005) {
      warnings.push(
        `total_sales (${totResult.value}) ≠ core_sales + catering_sales (${sum.toFixed(2)})`
      );
    }
  }

  return { errors, warnings };
}

export function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: 'utf-8',
      complete: resolve,
      error: reject,
    });
  });
}

export function mapAndNormalizeRows(papaResult) {
  const { data, meta } = papaResult;
  const fields = meta.fields || [];

  // Build header → canonical mapping
  const mapping = {};
  for (const field of fields) {
    const norm = normalizeHeader(field);
    const canon = HEADER_ALIASES[norm];
    if (canon && !(canon in Object.values(mapping))) {
      mapping[field] = canon;
    }
  }

  const found = new Set(Object.values(mapping));
  const missing = REQUIRED.filter(f => !found.has(f));
  if (missing.length) {
    return { error: `CSV is missing required column(s): ${missing.join(', ')}` };
  }

  const rows = data.map((rawRow, idx) => {
    const row = { _id: idx + 1, _lineNo: idx + 2 }; // lineNo: header=1, data starts at 2
    for (const [original, canonical] of Object.entries(mapping)) {
      row[canonical] = rawRow[original] ?? '';
    }

    // Normalize date in place
    const dateResult = parseDate(row.date);
    if (dateResult.value) row.date = dateResult.value;

    // Normalize numbers: strip $ and commas
    for (const field of ['core_sales', 'catering_sales', 'total_sales']) {
      const result = parseDecimal(row[field]);
      if (result.value !== null) row[field] = result.value;
    }

    return row;
  });

  return { rows };
}

export function findDuplicates(rows) {
  const seen = new Map();
  const dups = [];
  for (const row of rows) {
    const key = `${String(row.company_id || '').trim()}|${String(row.date || '').trim()}`;
    if (seen.has(key)) {
      dups.push({ lineNo: row._lineNo, key, otherLineNo: seen.get(key) });
    } else {
      seen.set(key, row._lineNo);
    }
  }
  return dups;
}

export function rowsToCsv(rows) {
  const fields = ['company_id', 'date', 'core_sales', 'catering_sales', 'total_sales'];
  return Papa.unparse(rows.map(r => {
    const out = {};
    for (const f of fields) out[f] = r[f] ?? '';
    return out;
  }), { columns: fields });
}
